import type { ElectronAPI } from '../../types/electron';
import { getProjectFiles } from './projectDetection';
import { readFileContentForUpload } from './fileReadingUtils';
import {
  prepareFileMetadataForUpload,
  detectPluginsFromFiles,
  pushVersionAndUpload,
  createUploadAbortError,
  isUploadAbortError,
  throwIfUploadAborted,
} from './uploadUtils';
import type { DetectedProject } from './projectDetection';
import type { TransferJobLifecycleCallbacks } from '@/context/TransferManagerContext';

export interface ProjectUploadItem {
  detectedProject: DetectedProject;
  projectName: string;
  description: string;
  rendersFolderName?: string;
}

export interface CreatedProject {
  projectId: string;
  folderPath: string;
  projectName: string;
}

export interface UploadProgressCallback {
  (projectName: string, progress: number): void;
}

export interface UploadLifecycleCallbacks {
  onContext?: TransferJobLifecycleCallbacks['onContext'];
  onDiagnostic?: TransferJobLifecycleCallbacks['onDiagnostic'];
  onStatusMessage?: (message: string) => void;
  onFileStart?: (fileName: string) => void;
  onFileProgress?: (fileName: string, progress: number) => void;
  onFileComplete?: (fileName: string) => void;
  onFileError?: (fileName: string, error: string) => void;
  getAbortSignal?: () => AbortSignal | undefined;
}

// Type for Apollo mutation functions - use Function type to accept any mutation function signature
export type CreateProjectMutation = (...args: any[]) => Promise<any>;
export type ProjectVersionPushMutation = (...args: any[]) => Promise<any>;
export type DeleteProjectMutation = (...args: any[]) => Promise<any>;

/**
 * Read file content for Electron files that were loaded in metadata-only mode
 */
async function readElectronFileContents(
  files: File[],
  isElectron: boolean,
  electronAPI: ElectronAPI | null | undefined
): Promise<File[]> {
  if (!isElectron || !electronAPI) {
    return files;
  }

  return Promise.all(
    files.map(async (file) => {
      const fileWithPath = file as File & {
        _electronFilePath?: string;
      };
      // Check if this file was loaded in metadata-only mode
      if (fileWithPath._electronFilePath) {
        try {
          // Read the actual file content
          return await readFileContentForUpload(
            electronAPI,
            fileWithPath._electronFilePath,
            file
          );
        } catch (error) {
          const reason = error instanceof Error ? error.message : String(error);
          throw new Error(
            `Failed to read file for upload: ${fileWithPath._electronFilePath} (${reason})`
          );
        }
      }
      return file;
    })
  );
}

/**
 * Upload a single project in a batch upload
 */
export async function uploadSingleProject(
  projectItem: ProjectUploadItem,
  uploadFiles: FileList,
  isElectron: boolean,
  electronAPI: ElectronAPI | null | undefined,
  createProject: CreateProjectMutation,
  projectVersionPush: ProjectVersionPushMutation,
  projectVersionFinalize: import('./uploadUtils').VersionPushOptions['finalizeMutation'],
  deleteProject: DeleteProjectMutation,
  onProgress: UploadProgressCallback,
  lifecycleCallbacks?: UploadLifecycleCallbacks
): Promise<CreatedProject> {
  const abortSignal = lifecycleCallbacks?.getAbortSignal?.();
  let newProjectId: string | undefined;
  let cleanupAttempted = false;

  const cleanupCreatedProject = async () => {
    if (!newProjectId || cleanupAttempted) return;
    cleanupAttempted = true;

    console.log(
      `Attempting to delete partially created project ${newProjectId} due to upload cancellation/failure.`
    );

    try {
      await deleteProject({
        variables: {
          projectId: newProjectId,
        },
      });
      console.log(`Successfully deleted project ${newProjectId}.`);
    } catch (deleteError) {
      lifecycleCallbacks?.onDiagnostic?.('Cleanup new project', deleteError);
      console.error(
        `Failed to delete project ${newProjectId} after upload failure/cancellation:`,
        deleteError
      );
    }
  };

  // Initialize progress
  onProgress(projectItem.projectName, 0);
  lifecycleCallbacks?.onStatusMessage?.('Creating Project');

  throwIfUploadAborted(abortSignal);

  // Create the project first
  const createResult = await createProject({
    variables: {
      data: {
        name: projectItem.projectName,
        description: projectItem.description,
        tags: [],
      },
    },
  });

  newProjectId = createResult.data?.projectCreate._id;
  if (!newProjectId) {
    throw new Error(`Failed to create project: ${projectItem.projectName}`);
  }
  lifecycleCallbacks?.onContext?.({ projectId: newProjectId });

  if (abortSignal?.aborted) {
    await cleanupCreatedProject();
    throw createUploadAbortError();
  }

  // Get files for this specific project
  let projectFiles = getProjectFiles(
    uploadFiles,
    projectItem.detectedProject.folderPath
  );

  // For Electron files loaded in metadata-only mode, read content now
  projectFiles = await readElectronFileContents(
    projectFiles,
    isElectron,
    electronAPI
  );

  // Prepare file metadata for version creation
  const fileMetadata = await prepareFileMetadataForUpload(
    projectFiles,
    projectItem.detectedProject.folderPath
  );

  // Detect plugins and build filesToUpload with pre-computed hashes (no double hash)
  const allDetectedPlugins = await detectPluginsFromFiles(projectFiles);
  const filesToUpload = projectFiles.map((file, i) => ({
    file,
    fullHash: fileMetadata[i].fullHash as string,
    fileName: fileMetadata[i].fileName,
  }));

  onProgress(projectItem.projectName, 20);
  lifecycleCallbacks?.onStatusMessage?.('Creating Version');

  if (abortSignal?.aborted) {
    await cleanupCreatedProject();
    throw createUploadAbortError();
  }

  try {
    // Create version and upload files to S3
    await pushVersionAndUpload({
      onContext: lifecycleCallbacks?.onContext,
      onDiagnostic: lifecycleCallbacks?.onDiagnostic,
      projectId: newProjectId,
      commitData: {
        commitMessage: 'Initial upload',
        newFiles: fileMetadata,
        updatedFiles: [],
        deletedFileIds: [],
        plugins: allDetectedPlugins,
      },
      filesToUpload,
      pushMutation: projectVersionPush,
      finalizeMutation: projectVersionFinalize,
      onUploadError: cleanupCreatedProject,
      onProgress: (pct) => {
        onProgress(projectItem.projectName, 20 + Math.round((pct * 80) / 100));
      },
      onStatusMessage: lifecycleCallbacks?.onStatusMessage,
      onFileStart: lifecycleCallbacks?.onFileStart,
      onFileProgress: lifecycleCallbacks?.onFileProgress,
      onFileComplete: lifecycleCallbacks?.onFileComplete,
      onFileError: lifecycleCallbacks?.onFileError,
      abortSignal,
    });
  } catch (error) {
    if (isUploadAbortError(error)) {
      await cleanupCreatedProject();
      throw createUploadAbortError();
    }
    throw error;
  }

  onProgress(projectItem.projectName, 100);

  // Create the renders folder locally in Electron if the user chose one and none was detected
  if (
    isElectron &&
    electronAPI &&
    projectItem.rendersFolderName &&
    !projectItem.detectedProject.rendersFolder
  ) {
    const folderPath = projectItem.detectedProject.folderPath;
    let baseAbsPath: string | null = null;
    for (let i = 0; i < uploadFiles.length; i++) {
      const file = uploadFiles[i] as File & {
        webkitRelativePath?: string;
        _electronFilePath?: string;
      };
      const relPath = file.webkitRelativePath;
      const absPath = file._electronFilePath;
      if (absPath && relPath && relPath.startsWith(folderPath + '/')) {
        // absPath = <basePath>/<relPath>
        baseAbsPath = absPath.slice(0, absPath.length - relPath.length - 1);
        break;
      }
    }
    if (baseAbsPath) {
      const rendersPath = `${baseAbsPath}/${folderPath}/${projectItem.rendersFolderName}/.gitkeep`;
      await electronAPI.writeFile(rendersPath, '').catch(() => {
        // Non-fatal: folder creation failure doesn't affect the upload
      });
    }
  }

  return {
    projectId: newProjectId,
    folderPath: projectItem.detectedProject.folderPath,
    projectName: projectItem.projectName,
  };
}

/**
 * Upload multiple projects in a batch
 */
export async function uploadBatchProjects(
  selectedProjects: ProjectUploadItem[],
  uploadFiles: FileList,
  isElectron: boolean,
  electronAPI: ElectronAPI | null | undefined,
  createProject: CreateProjectMutation,
  projectVersionPush: ProjectVersionPushMutation,
  projectVersionFinalize: import('./uploadUtils').VersionPushOptions['finalizeMutation'],
  deleteProject: DeleteProjectMutation,
  onProgress: UploadProgressCallback,
  onError: (projectName: string, error: Error) => void
): Promise<CreatedProject[]> {
  const createdProjects: CreatedProject[] = [];

  for (const projectItem of selectedProjects) {
    try {
      const created = await uploadSingleProject(
        projectItem,
        uploadFiles,
        isElectron,
        electronAPI,
        createProject,
        projectVersionPush,
        projectVersionFinalize,
        deleteProject,
        onProgress
      );
      createdProjects.push(created);
    } catch (error) {
      console.error(
        `Error creating/uploading project ${projectItem.projectName}:`,
        error
      );
      onError(
        projectItem.projectName,
        error instanceof Error ? error : new Error(String(error))
      );
      // Mark as error (-1 indicates error)
      onProgress(projectItem.projectName, -1);
    }
  }

  return createdProjects;
}
